import { useContext, useState, useEffect, useMemo } from "react";
import { PlaceholderBundleContext, ExtendedPlaceholderStepsType, PlaceholderMap } from "./PlaceholderBundle";
import ProductCard from "./PlaceholderProductCard";
import {
    fetchCollectionProducts,
    filterInStockProducts,
    updateBundleConfigWithRealTimeProducts,
} from "../../constants";
import {
    getCurrentPageSelectCount,
    getCurrentPageSelectTotal,
    getPlaceholderCardWidth,
    getGapForSize,
    checkForStepConditions,
    showPlaceholderItem,
    applyConditionsBB,
    flatProductAsVariants,
} from "./PlaceholderConstants";
import styles from "./PlaceholderAccordianTemplate.module.css";
import { InspectorEditableBlock } from "../../CommonFields/InspectorEditableBlock";
import BundleProgressBar from "../../BundleBuilder/BundleBuilderBundle/BundleProgressBar";

const ChevronUp = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M2 9L7 4L12 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const PlusIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M7 2V12M2 7H12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const SmallCheckIcon = ({ size = 12 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
        <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const SearchIcon = () => (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
        <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5" />
        <path d="M9.5 9.5l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

const SubStepPills = ({
    subSteps,
    activeSubStep,
    setActiveSubStep,
    stepIndex,
}: {
    subSteps: any[];
    activeSubStep: number;
    setActiveSubStep: (i: number) => void;
    stepIndex: number;
}) => {
    const { bundleConfig, selectedVariants } = useContext(PlaceholderBundleContext);

    return (
        <div className={styles.pillsRow}>
            {subSteps.map((ss: any, i: number) => {
                const subVariants = selectedVariants.filter((v: any) => v.page === stepIndex && v.subStepIdx === i);
                const isSSDone = subVariants.length > 0;

                return (
                    <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="addToCartCta">
                        <button
                            key={i}
                            className={styles.pill}
                            style={{
                                background:
                                    activeSubStep === i
                                        ? bundleConfig?.bundles_design?.add_to_cart_bg_color
                                        : bundleConfig?.bundles_design?.add_to_cart_text_color,
                                color:
                                    activeSubStep === i
                                        ? bundleConfig?.bundles_design?.add_to_cart_text_color
                                        : bundleConfig?.bundles_design?.add_to_cart_bg_color,
                                border: `1.5px solid ${activeSubStep === i ? bundleConfig?.bundles_design?.add_to_cart_text_color : bundleConfig?.bundles_design?.add_to_cart_bg_color}`,
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                fontWeight: 600,
                            }}
                            onClick={() => setActiveSubStep(i)}
                        >
                            {ss?.step_label || ss?.label || `Option ${i + 1}`}
                            {isSSDone && <SmallCheckIcon size={10} />}
                        </button>
                    </InspectorEditableBlock>
                );
            })}
        </div>
    );
};

const AccordionStepRow = ({
    stepIndex,
    step,
    placeholderItem,
    layout,
    handleAddProduct,
}: {
    stepIndex: number;
    step: ExtendedPlaceholderStepsType;
    placeholderItem: PlaceholderMap;
    layout: string;
    handleAddProduct: any;
}) => {
    const {
        bundleConfig,
        selectedVariants,
        isMobile,
        setActiveStep,
        setActiveSubStep: setGlobalActiveSubStep,
        sft,
        standAlone,
        allStepErrors,
        hasClickedAddToCartButton,
    } = useContext(PlaceholderBundleContext);

    const [searchTerm, setSearchTerm] = useState("");
    const [localActiveSubStep, setLocalActiveSubStep] = useState<number>(0);
    const [rawProducts, setRawProducts] = useState<any[]>([]);
    const [listProducts, setListProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(stepIndex < 2);

    const filteredProducts = useMemo(() => {
        const query = searchTerm?.trim()?.toLowerCase() || "";
        if (!query) return listProducts;

        return listProducts.filter((product: any) => {
            if (product.handle?.toLowerCase().includes(query) || product.title?.toLowerCase().includes(query)) {
                return true;
            } else if (product.variants?.length) {
                return product.variants.some((variant: any) => variant?.title?.toLowerCase()?.includes(query));
            }
            return false;
        });
    }, [listProducts, searchTerm]);

    const currCount = getCurrentPageSelectCount(selectedVariants, stepIndex);
    const currTotal = getCurrentPageSelectTotal(selectedVariants, stepIndex);

    const conditions = step?.step_conditions || [];

    const isDone = useMemo(() => {
        if (step?.show_step_conditions === false) return;
        return checkForStepConditions(conditions as any, currCount, currTotal);
    }, [step, conditions, currCount, currTotal]);

    const disableAdding = useMemo(() => {
        if (step?.show_step_conditions === false) return false;
        return !showPlaceholderItem(conditions as any, currCount, currTotal);
    }, [step, conditions, currCount, currTotal]);

    const cardWidth = getPlaceholderCardWidth(
        isMobile
            ? bundleConfig?.product_card_mobile_size || "medium"
            : bundleConfig?.product_card_desktop_size || "medium",
        isMobile ? "mobile" : "desktop",
    );
    const cardGap = getGapForSize(
        isMobile ? bundleConfig?.product_card_mobile_size : bundleConfig?.product_card_desktop_size,
        isMobile,
    );

    const max = placeholderItem?.max ?? null;
    const isExactCondition = conditions?.some((c: any) => c.type === "exact_products");
    const selectedLabel =
        step?.show_step_conditions === false || !isExactCondition
            ? ""
            : max != null
              ? `${currCount} of ${max} selected`
              : `${currCount} selected`;

    const handleLocalSubStepChange = (i: number) => {
        setLocalActiveSubStep(i);
        setGlobalActiveSubStep(i);
    };

    useEffect(() => {
        if (!open) return;
        loadProducts();
    }, [open, localActiveSubStep, step]);

    useEffect(() => {
        if (!open || rawProducts.length === 0) {
            setListProducts([]);
            return;
        }

        let prods = [...rawProducts];
        prods = flatProductAsVariants(prods, !!step?.render_as_variants);

        if (bundleConfig?.hide_out_of_stock_products) {
            prods = prods.filter(filterInStockProducts);
        }

        if (bundleConfig?.allow_conditional_logic) {
            prods = applyConditionsBB(bundleConfig?.product_conditions, selectedVariants, prods);
        }

        if (bundleConfig?.move_selected_products_first) {
            const selectedProductQuantities = selectedVariants
                .filter((v: any) => v.page === stepIndex)
                .reduce((acc: any, v: any) => {
                    acc[v.parentProduct] = (acc[v.parentProduct] || 0) + (v.qty || 1);
                    return acc;
                }, {});

            prods.sort((a, b) => {
                const qtyA = selectedProductQuantities[a.id] || 0;
                const qtyB = selectedProductQuantities[b.id] || 0;

                if (qtyA > 0 && qtyB > 0) {
                    return qtyB - qtyA;
                }
                if (qtyA > 0) return -1;
                if (qtyB > 0) return 1;
                return 0;
            });
        }

        setListProducts(prods);
    }, [
        open,
        rawProducts,
        selectedVariants,
        bundleConfig?.hide_out_of_stock_products,
        bundleConfig?.allow_conditional_logic,
        bundleConfig?.product_conditions,
        bundleConfig?.move_selected_products_first,
        step?.render_as_variants,
    ]);

    const loadProducts = async () => {
        setLoading(true);
        try {
            let prods: any[] = [];
            if (step?.enable_subSteps) {
                const stepProds = await loadFromStep(step.subSteps?.[localActiveSubStep]);
                prods = stepProds.map((p) => ({ ...p, subStepIdx: localActiveSubStep }));
            } else {
                prods = await loadFromStep(step);
            }

            setRawProducts(prods);
        } catch (err) {
            console.error("Accordion loadProducts error:", err);
            setRawProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const loadFromStep = async (s: any): Promise<any[]> => {
        if (!s) return [];
        if (s?.product_selection === "collection") {
            const id = (s?.collections?.[0]?.id || "").split("/").pop() || "";
            if (!id) return [];
            const res = await fetchCollectionProducts(id, sft, () => {}, standAlone, 50, null);
            return res?.products || [];
        }
        if (s?.product_selection === "specific") {
            const newConfig = await updateBundleConfigWithRealTimeProducts(
                sft,
                [{ ...bundleConfig, products: [...(s?.products || [])] }],
                standAlone,
            );
            let list = newConfig[0]?.products || [];
            list = list.map((item: any) => {
                const main = (s?.products || []).find((m: any) => m.id === item.id);
                return {
                    ...item,
                    variants: main
                        ? item.variants.filter((v: any) => main.variants.findIndex((t: any) => t.id === v.id) >= 0)
                        : item.variants,
                };
            });
            return list;
        }
        return [];
    };

    const getSubStepIdxForProduct = (product: any): number => {
        if (typeof product.subStepIdx === "number") return product.subStepIdx;
        if (!step?.enable_subSteps) return 0;
        return localActiveSubStep;
    };

    const hasSubSteps = step?.enable_subSteps && (step?.subSteps?.length || 0) > 0;

    const handleToggle = () => {
        if (!open) setActiveStep(stepIndex);
        setOpen((o) => !o);
    };

    return (
        <div
            className={styles.accordionRow}
            style={{ borderBottom: `1px solid ${bundleConfig?.bundles_design?.product_border_color}` }}
        >
            <div className={styles.accordionHeader} onClick={handleToggle}>
                <div className={styles.stepMeta}>
                    <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="stepLabel">
                        <div
                            className={styles.stepTitle}
                            style={{
                                color: bundleConfig?.bundles_design?.step_label_color,
                                fontSize: bundleConfig?.bundles_design?.step_label_size,
                                fontWeight: bundleConfig?.bundles_design?.step_label_style?.split(" ")[0] || "600",
                                fontStyle: bundleConfig?.bundles_design?.step_label_style?.split(" ")[1] || "normal",
                            }}
                        >
                            {step?.step_label || `Step ${stepIndex + 1}`}
                        </div>
                    </InspectorEditableBlock>
                    <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="stepSubTitle">
                        <div
                            className={styles.stepTitle}
                            style={{
                                color: bundleConfig?.bundles_design?.step_page_subtitle_color,
                                fontSize: bundleConfig?.bundles_design?.step_page_subtitle_size,
                                fontWeight:
                                    bundleConfig?.bundles_design?.step_page_subtitle_style?.split(" ")[0] || "600",
                                fontStyle:
                                    bundleConfig?.bundles_design?.step_page_subtitle_style?.split(" ")[1] || "normal",
                            }}
                        >
                            {step?.page_subtitle}
                        </div>
                    </InspectorEditableBlock>
                    <span className={styles.stepProgress} style={{ color: isDone ? "green" : "red" }}>
                        {selectedLabel}
                    </span>
                </div>

                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="addToCartCta">
                    <div
                        className={styles.toggleBtn}
                        style={{
                            backgroundColor: open
                                ? bundleConfig?.bundles_design?.add_to_cart_text_color
                                : bundleConfig?.bundles_design?.add_to_cart_bg_color,
                            color: open
                                ? bundleConfig?.bundles_design?.add_to_cart_bg_color
                                : bundleConfig?.bundles_design?.add_to_cart_text_color,
                        }}
                    >
                        {open ? <ChevronUp /> : <PlusIcon />}
                    </div>
                </InspectorEditableBlock>
            </div>

            {open && (
                <div className={styles.accordionBody}>
                    {hasSubSteps && (
                        <SubStepPills
                            subSteps={step.subSteps || []}
                            activeSubStep={localActiveSubStep}
                            setActiveSubStep={handleLocalSubStepChange}
                            stepIndex={stepIndex}
                        />
                    )}

                    {bundleConfig?.show_search_bar && (
                        <div className={styles.searchWrap}>
                            <div className={styles.searchInner}>
                                <SearchIcon />
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder="Search products..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <div className={styles.loadingRow}>
                            <span
                                className={styles.spinner}
                                style={{ borderTopColor: bundleConfig?.bundles_design?.add_to_cart_bg_color }}
                            />
                        </div>
                    ) : filteredProducts.length === 0 ? (
                        <p className={styles.emptyText}>No products available</p>
                    ) : layout === "scroll" ? (
                        <HorizontalScrollView
                            cardGap={cardGap}
                            cardWidth={cardWidth}
                            listProducts={filteredProducts}
                            getSubStepIdxForProduct={getSubStepIdxForProduct}
                            stepIndex={stepIndex}
                            placeholderItem={placeholderItem}
                            disableAdding={disableAdding}
                            step={step}
                            handleAddProduct={handleAddProduct}
                        />
                    ) : (
                        <GridView
                            cardGap={cardGap}
                            cardWidth={cardWidth}
                            listProducts={filteredProducts}
                            getSubStepIdxForProduct={getSubStepIdxForProduct}
                            stepIndex={stepIndex}
                            placeholderItem={placeholderItem}
                            disableAdding={disableAdding}
                            step={step}
                            handleAddProduct={handleAddProduct}
                        />
                    )}
                </div>
            )}
            {hasClickedAddToCartButton &&
                allStepErrors
                    ?.find((e: any) => e.stepIndex === stepIndex)
                    ?.errors?.map((e: string, idx: number) => (
                        <p
                            key={idx}
                            style={{
                                margin: "10px 15px",
                                padding: 0,
                                lineHeight: "normal",
                                fontSize: 13,
                                color: "red",
                            }}
                        >
                            {e}
                        </p>
                    ))}
        </div>
    );
};

export const PlaceholderAccordionTemplate = ({
    steps,
    placeholderMap,
    handleAddProduct,
}: {
    steps: ExtendedPlaceholderStepsType[];
    placeholderMap: PlaceholderMap[];
    handleAddProduct: any;
}) => {
    const {
        bundleConfig,
        totalPrice,
        normalTotalQuantity,
        currency,
        money_format,
        tierSuccessMessage,
        actualConfigDiscountType,
    } = useContext(PlaceholderBundleContext);

    // const doneSteps = useMemo(
    //     () =>
    //         steps.filter((step, idx) => {
    //             const count = getCurrentPageSelectCount(selectedVariants, idx);
    //             const total = getCurrentPageSelectTotal(selectedVariants, idx);
    //             const conditions = step.gift_step
    //                 ? [
    //                       {
    //                           basis: "items_count",
    //                           type: step?.step_condition_type,
    //                           limit: +(step as any)?.product_count_limit,
    //                       },
    //                   ]
    //                 : step?.step_conditions || [];
    //             if (step?.show_step_conditions === false) return count >= 0;
    //             return checkForStepConditions(conditions as any, count, total);
    //         }).length,
    //     [selectedVariants, steps],
    // );

    // const showProgress = useMemo(() => {
    //     return steps.some((step) => {
    //         if (step?.show_step_conditions === false) return false;
    //         if (step.gift_step) {
    //             return step?.step_condition_type === "min_products" || step?.step_condition_type === "exact_products";
    //         }
    //         return step?.step_conditions?.some((c: any) => c.type === "min_products" || c.type === "exact_products");
    //     });
    // }, [steps]);

    return (
        <div className={styles.accordionTemplateRoot}>
            <div className={styles.accordionTemplateHeader}>
                <div className={styles.headerLeft}>
                    <InspectorEditableBlock settingKey="bundleTitle" bundleType={bundleConfig?.bundle_type}>
                        <p
                            className={styles.widgetTitle}
                            style={{
                                color: bundleConfig?.bundles_design?.bundle_title_color || "#111",
                                fontSize: bundleConfig?.bundles_design?.bundle_title_size,
                                fontWeight: bundleConfig?.bundles_design?.bundle_title_style?.split(" ")[0] || "700",
                                margin: 0,
                            }}
                        >
                            {bundleConfig?.title}
                        </p>
                    </InspectorEditableBlock>

                    {/* {showProgress && (
                        <span
                            className={styles.progressSubtitle}
                            style={{ color: doneSteps === steps.length ? "green" : "red" }}
                        >
                            {doneSteps} of {steps.length} steps done
                        </span>
                    )} */}
                </div>
            </div>

            <div className={styles.divider} />

            {steps.map((step, idx) => (
                <AccordionStepRow
                    key={idx}
                    stepIndex={idx}
                    step={step}
                    placeholderItem={placeholderMap[idx]}
                    layout={bundleConfig?.accordion_product_layout}
                    handleAddProduct={handleAddProduct}
                />
            ))}

            <BundleProgressBar
                bundleConfig={bundleConfig}
                totalPrice={totalPrice}
                totalQuantity={normalTotalQuantity}
                currency={currency}
                money_format={money_format}
                tierSuccessMessage={tierSuccessMessage}
                actualConfigDiscountType={actualConfigDiscountType}
                messagePlacement="bottom"
            />
        </div>
    );
};

type ProductViewProps = {
    cardGap: string;
    cardWidth: string;
    listProducts: any[];
    getSubStepIdxForProduct: (product: any) => number;
    stepIndex: number;
    placeholderItem: PlaceholderMap;
    disableAdding: boolean;
    step: any;
    handleAddProduct: any;
};

const GridView = ({
    cardGap,
    cardWidth,
    listProducts,
    getSubStepIdxForProduct,
    stepIndex,
    placeholderItem,
    disableAdding,
    step,
    handleAddProduct,
}: ProductViewProps) => {
    return (
        <div
            className={styles.gridWrap}
            style={{
                gap: cardGap,
                gridTemplateColumns: `repeat(auto-fit, calc(${cardWidth} * 0.8))`,
            }}
        >
            {listProducts.map((product: any, idx: number) => {
                const subIdx = getSubStepIdxForProduct(product);
                return (
                    <div
                        key={product.id + (product.variants?.[0]?.id || idx)}
                        style={{
                            width: `calc(${cardWidth} * 0.8)`,
                            display: "flex",
                        }}
                    >
                        <div
                            style={{
                                transform: "scale(0.8)",
                                transformOrigin: "top left",
                                width: cardWidth,
                                display: "flex",
                                flexDirection: "column",
                                marginBottom: `calc(${cardWidth} * -0.22)`,
                            }}
                        >
                            <ProductCard
                                index={idx}
                                product={product}
                                setTrackVariant={() => {}}
                                handleAddProduct={(_: number, prod: any, optType: string, ssIdx?: number) =>
                                    handleAddProduct(stepIndex, prod, optType, ssIdx ?? subIdx)
                                }
                                isFreeGift={placeholderItem?.type === "free"}
                                disableCtas={disableAdding}
                                currentStep={step}
                                activeSubStep={subIdx}
                                isAccorianTemplate={true}
                                stepIndex={stepIndex}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const HorizontalScrollView = ({
    cardGap,
    cardWidth,
    listProducts,
    getSubStepIdxForProduct,
    stepIndex,
    placeholderItem,
    disableAdding,
    step,
    handleAddProduct,
}: ProductViewProps) => {
    return (
        <div className={styles.hScrollOuter}>
            <div
                className={styles.hScrollInner}
                style={{
                    gap: cardGap,
                    display: "flex",
                    flexWrap: "nowrap",
                }}
            >
                {listProducts.map((product: any, idx: number) => {
                    const subIdx = getSubStepIdxForProduct(product);
                    return (
                        <div
                            key={product.id + (product.variants?.[0]?.id || idx)}
                            className={styles.hScrollCard}
                            style={{
                                width: `calc(${cardWidth} * 0.8)`,
                                display: "flex",
                            }}
                        >
                            <div
                                style={{
                                    transform: "scale(0.8)",
                                    transformOrigin: "top left",
                                    width: cardWidth,
                                    display: "flex",
                                    flexDirection: "column",
                                    marginBottom: `calc(${cardWidth} * -0.2)`,
                                }}
                                id={
                                    placeholderItem?.type === "free"
                                        ? `accordianFreeGiftCard_${idx}`
                                        : `accordianProductCard_${idx}`
                                }
                            >
                                <ProductCard
                                    index={idx}
                                    product={product}
                                    setTrackVariant={() => {}}
                                    handleAddProduct={(_: number, prod: any, optType: string, ssIdx?: number) =>
                                        handleAddProduct(stepIndex, prod, optType, ssIdx ?? subIdx)
                                    }
                                    isFreeGift={placeholderItem?.type === "free"}
                                    disableCtas={disableAdding}
                                    currentStep={step}
                                    activeSubStep={subIdx}
                                    isAccorianTemplate={true}
                                    stepIndex={stepIndex}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
